import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { get, post } from "../../../services/api";
import { toast } from "react-toastify";
import StudentElementsUI from "./StudentElementsUI";

const StudentElementsTab = ({ selectedStudent }) => {
  const token = localStorage.getItem("token");
  const studentId = selectedStudent.id;
  const rollNo = selectedStudent.rollNo;
  const [elementsData, setElementsData] = useState([]);
  const [fileCounts, setFileCounts] = useState({});
  const [fileUrls, setFileUrls] = useState({});

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      elements: [
        {
          name: "",
          description: "",
          createdBy: localStorage.getItem("userId"),
          elementDetail: {
            value: "",
            description: "",
            effectiveDate: null,
            endDate: null,
            createdBy: localStorage.getItem("userId"),
            fileName: "",
            filePath: "",
            base64String: "",
          },
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "elements",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch elements dropdown data
        const elementsResponse = await get(
          `Dropdown/GetElements?StudentId=${rollNo}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setElementsData(elementsResponse);

        // Fetch existing student elements
        const studentElementsData = await get(
          `InspStudentFirstRegistrtion/GetStudentElementByStudentId?studentId=${studentId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (studentElementsData) {
          const newFileCounts = {};
          const newFileUrls = {};
          
          const mappedElements = studentElementsData?.length > 0
            ? studentElementsData.map((element, index) => {
                const matchingElement = elementsResponse.find(
                  (item) => item.id === element.elementId
                );

                const filePath = element.filePath ? (element.filePath.startsWith("/") ? element.filePath : `/${element.filePath}`) : "";
                const fileUrl = filePath && element.fileName ? `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${filePath}` : "";

                newFileCounts[index] = element.fileName ? 1 : 0;
                newFileUrls[index] = fileUrl;

                return {
                  id: element.id || 0,
                  value: element.value || "",
                  name: matchingElement
                    ? { value: matchingElement.id, label: matchingElement.name }
                    : null,
                  description: element.description || "",
                  elementDetail: {
                    value: element.value || "",
                    description: element.description || "",
                    effectiveDate: element.effectiveDate?.split("T")[0] || null,
                    endDate: element.endDate?.split("T")[0] || null,
                    fileName: element.fileName || "",
                    filePath: element.filePath || "",
                    base64String: element.base64String || "",
                  },
                };
              })
            : [
                {
                  name: "",
                  description: "",
                  createdBy: localStorage.getItem("userId"),
                  elementDetail: {
                    value: "",
                    description: "",
                    effectiveDate: null,
                    endDate: null,
                    createdBy: localStorage.getItem("userId"),
                    fileName: "",
                    filePath: "",
                    base64String: "",
                  },
                },
              ];

          setFileCounts(newFileCounts);
          setFileUrls(newFileUrls);

          reset({
            elements: mappedElements,
          });
        }
      } catch (error) {
        console.error("Error fetching student elements data:", error);
      }
    };

    if (studentId && token) {
      fetchData();
    }
  }, [studentId, token, reset]);

  const handleAttachmentFileChange = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      setFileCounts((prevCounts) => ({ ...prevCounts, [index]: 1 }));
      setValue(`elements[${index}].elementDetail.fileName`, file.name, {
        shouldValidate: true,
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setValue(
          `elements[${index}].elementDetail.base64String`,
          base64String,
          { shouldValidate: true }
        );
        const fileUrl = URL.createObjectURL(file);
        setFileUrls((prevUrls) => ({ ...prevUrls, [index]: fileUrl }));
      };
      reader.readAsDataURL(file);
    } else {
      setFileCounts((prevCounts) => ({ ...prevCounts, [index]: 0 }));
      setValue(`elements[${index}].elementDetail.fileName`, "", {
        shouldValidate: true,
      });
      setValue(`elements[${index}].elementDetail.base64String`, "", {
        shouldValidate: true,
      });
      setValue(`elements[${index}].elementDetail.filePath`, "", {
        shouldValidate: true,
      });
      setFileUrls((prevUrls) => ({ ...prevUrls, [index]: "" }));
    }
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized");
      return;
    }

    const elementsDataPayload = data?.elements
      ?.map((element) => {
        if (!element.name || element.name.value === undefined) {
          return null;
        }

        return {
          id: element.id || 0,
          name: element.name?.label || element.name || "",
          description: element.description || "",
          createdBy:localStorage.getItem("userId"),
          elementDetail: {
            elementId: element.name.value || 0,
            value: element.elementDetail?.value || "",
            description: element.elementDetail?.description || "",
            effectiveDate: element.elementDetail?.effectiveDate || null,
            endDate: element.elementDetail?.endDate || null,
            fileName: element.elementDetail?.fileName || "",
            filePath: element.elementDetail?.filePath || "",
            base64String: element.elementDetail?.base64String || "",
            createdBy:localStorage.getItem("userId"),
          },
        };
      })
      .filter((element) => element !== null);

    try {
      const response = await post(`InspStudentFirstRegistrtion/AddStudentElements?studentId=${studentId}`, elementsDataPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        toast.success("Elements updated successfully!");
      } else {
        toast.error(response.message || "Failed to update elements.");
      }
    } catch (error) {
      console.error("Error saving student elements:", error);
      toast.error("An error occurred while saving.");
    }
  };

  return (
    <div className="profile-content-area d-flex flex-column h-100">
      <div className="mb-5">
        <h5 className="mb-0 fw-bold">Student Elements</h5>
      </div>

      <div className="profile-scrollable-area p-1 flex-grow-1">
        <form onSubmit={handleSubmit(saveData)}>
          <div className="card shadow-sm">
            <div className="card-body">
              <StudentElementsUI
                fields={fields}
                control={control}
                register={register}
                errors={errors}
                watch={watch}
                elementsData={elementsData}
                append={append}
                remove={remove}
                handleAttachmentFileChange={handleAttachmentFileChange}
                fileCounts={fileCounts}
                fileUrls={fileUrls}
                setValue={setValue}
                getValues={getValues}
                trigger={trigger}
              />
            </div>
          </div>

          <div className="mt-4 pt-3 d-flex justify-content-end gap-3">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span
                  className="spinner-border spinner-custom-size me-1"
                  aria-hidden="true"
                ></span>
              ) : null}
              Save Elements
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentElementsTab;
